// ============================================================================
// SECURITY FLAGS COMPONENT
// ============================================================================
// List and manage security flags matching AdminDashboard design system.
// ============================================================================

import React, { useEffect, useState, useMemo } from 'react';
import {
  VStack,
  HStack,
  Text,
  Badge,
  Spinner,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import {
  Flag,
  AlertTriangle,
  RefreshCw,
  Search,
  CheckCircle,
  Filter,
  ArrowUpDown,
  Eye,
} from 'lucide-react';
import { POKER_COLORS } from '../../../theme/designSystem';
import PokerCard from '../../ui/PokerCard';
import PokerButton from '../../ui/PokerButton';
import { securityFlagService } from '../../../services/security';
import { SecurityFlag, FlagStatus, SecurityFlagType, FlagSeverity } from '../../../types/securityTypes';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SecurityFlags: React.FC = () => {
  const [flags, setFlags] = useState<SecurityFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'severity' | 'confidence'>('date');

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);

      let loadedFlags: SecurityFlag[];
      
      if (statusFilter !== 'all') {
        loadedFlags = await securityFlagService.getFlagsByStatus(statusFilter as FlagStatus);
      } else {
        loadedFlags = await securityFlagService.getAllFlags();
      }

      setFlags(loadedFlags);

    } catch (err) {
      console.error('Failed to load flags:', err);
      setError('Failed to load security flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlags();
  }, [statusFilter]);

  // ============================================================================
  // FILTERING & SORTING
  // ============================================================================

  const filteredFlags = useMemo(() => {
    let result = [...flags];

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(f => f.type === typeFilter);
    }

    // Severity filter
    if (severityFilter !== 'all') {
      result = result.filter(f => f.severity === severityFilter);
    }

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(f => 
        f.primaryUserId?.toLowerCase().includes(query) ||
        f.type?.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'severity':
          const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          return (severityOrder[a.severity] || 4) - (severityOrder[b.severity] || 4);
        case 'confidence':
          return (b.confidence || 0) - (a.confidence || 0);
        case 'date':
        default:
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
      }
    });

    return result;
  }, [flags, typeFilter, severityFilter, searchQuery, sortBy]);

  // ============================================================================
  // HELPERS
  // ============================================================================

  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getSeverityBadge = (severity: string) => {
    const colors: Record<string, string> = {
      critical: 'red',
      high: 'orange',
      medium: 'yellow',
      low: 'gray',
    };
    return <Badge colorScheme={colors[severity] || 'gray'}>{severity.toUpperCase()}</Badge>;
  };

  const getTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      collusion: 'Collusion',
      multi_account: 'Multi-Account',
      bot: 'Bot',
      chip_dumping: 'Chip Dump',
      same_ip_table: 'Same IP',
      suspicious_timing: 'Timing',
      unusual_pattern: 'Unusual',
    };
    const colors: Record<string, string> = {
      collusion: 'purple',
      multi_account: 'red',
      bot: 'orange',
      chip_dumping: 'yellow',
      same_ip_table: 'green',
      suspicious_timing: 'blue',
      unusual_pattern: 'cyan',
    };
    return <Badge colorScheme={colors[type] || 'gray'}>{labels[type] || type}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: 'red',
      reviewing: 'yellow',
      resolved: 'green',
      dismissed: 'gray',
    };
    return <Badge colorScheme={colors[status] || 'gray'}>{status.toUpperCase()}</Badge>;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <PokerCard>
        <VStack py={8}>
          <Spinner size="xl" color={POKER_COLORS.cyan} />
          <Text color="gray.400">Loading security flags...</Text>
        </VStack>
      </PokerCard>
    );
  }

  if (error) {
    return (
      <PokerCard>
        <VStack py={8}>
          <AlertTriangle size={48} color="#E53E3E" />
          <Text color="red.400">{error}</Text>
          <PokerButton variant="secondary" size="sm" onClick={loadFlags} leftIcon={<RefreshCw size={14} />}>
            Retry
          </PokerButton>
        </VStack>
      </PokerCard>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header */}
      <PokerCard>
        <HStack justify="space-between" mb={4}>
          <HStack spacing={3}>
            <Flag size={24} color={POKER_COLORS.cyan} />
            <VStack align="start" spacing={0}>
              <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="lg">Security Flags</Text>
              <Text color="gray.500" fontSize="sm">{filteredFlags.length} flags</Text>
            </VStack>
          </HStack>
          <PokerButton variant="secondary" size="sm" leftIcon={<RefreshCw size={14} />} onClick={loadFlags}>
            Refresh
          </PokerButton>
        </HStack>

        {/* Filters */}
        <HStack spacing={4} flexWrap="wrap">
          <InputGroup size="sm" maxW="200px">
            <InputLeftElement>
              <Search size={14} color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="gray.800"
              border="1px solid"
              borderColor="gray.700"
              color="white"
              _placeholder={{ color: 'gray.500' }}
            />
          </InputGroup>

          <Select
            size="sm"
            maxW="150px"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            bg="gray.800"
            border="1px solid"
            borderColor="gray.700"
            color="white"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </Select>

          <Select
            size="sm"
            maxW="150px"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            bg="gray.800"
            border="1px solid"
            borderColor="gray.700"
            color="white"
          >
            <option value="all">All Types</option>
            <option value="collusion">Collusion</option>
            <option value="multi_account">Multi-Account</option>
            <option value="bot">Bot</option>
            <option value="chip_dumping">Chip Dumping</option>
            <option value="same_ip_table">Same IP</option>
            <option value="suspicious_timing">Timing</option>
          </Select>

          <Select
            size="sm"
            maxW="150px"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            bg="gray.800"
            border="1px solid"
            borderColor="gray.700"
            color="white"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </Select>

          <Select
            size="sm"
            maxW="150px"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'severity' | 'confidence')}
            bg="gray.800"
            border="1px solid"
            borderColor="gray.700"
            color="white"
          >
            <option value="date">Sort by Date</option>
            <option value="severity">Sort by Severity</option>
            <option value="confidence">Sort by Confidence</option>
          </Select>
        </HStack>
      </PokerCard>

      {/* Flags Table */}
      <PokerCard>
        {filteredFlags.length === 0 ? (
          <VStack py={8}>
            <CheckCircle size={48} color="#38A169" />
            <Text color="gray.500">No flags found</Text>
            <Text color="gray.600" fontSize="sm">Try adjusting your filters</Text>
          </VStack>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color="gray.500">Status</Th>
                  <Th color="gray.500">Type</Th>
                  <Th color="gray.500">Severity</Th>
                  <Th color="gray.500">Player</Th>
                  <Th color="gray.500">Confidence</Th>
                  <Th color="gray.500">Created</Th>
                  <Th color="gray.500">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredFlags.map(flag => (
                  <Tr key={flag.id} _hover={{ bg: 'gray.800' }}>
                    <Td>{getStatusBadge(flag.status)}</Td>
                    <Td>{getTypeBadge(flag.type)}</Td>
                    <Td>{getSeverityBadge(flag.severity)}</Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text color="white" fontSize="sm">{flag.primaryUserId?.substring(0, 12)}...</Text>
                        {flag.relatedUserId && (
                          <Text color="gray.500" fontSize="xs">+ {flag.relatedUserId?.substring(0, 8)}...</Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <HStack>
                        <Box 
                          w="40px" 
                          h="6px" 
                          bg="gray.700" 
                          borderRadius="full"
                          overflow="hidden"
                        >
                          <Box
                            w={`${flag.confidence}%`}
                            h="100%"
                            bg={flag.confidence >= 80 ? 'red.400' : flag.confidence >= 60 ? 'orange.400' : 'yellow.400'}
                          />
                        </Box>
                        <Text color={POKER_COLORS.gold} fontSize="sm">{flag.confidence}%</Text>
                      </HStack>
                    </Td>
                    <Td color="gray.400" fontSize="sm">{formatTime(flag.createdAt)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <PokerButton size="xs" variant="secondary" leftIcon={<Eye size={12} />}>
                          View
                        </PokerButton>
                        {(flag.status === 'new' || flag.status === 'reviewing') && (
                          <PokerButton size="xs" variant="primary">
                            Resolve
                          </PokerButton>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </PokerCard>
    </VStack>
  );
};

export default SecurityFlags;